import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Star, Camera, Wrench, ExternalLink } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { timeZone: "America/Toronto", dateStyle: "medium", timeStyle: "short" }) : "—");
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/Toronto", dateStyle: "medium" }) : "—");

// status → [label, button classes, star fill]
const STATUS = {
  sent: ["Review cycle running", "border-amber-400 bg-amber-50 text-amber-900", "#f59e0b"],
  clicked: ["Review link clicked ✓", "border-emerald-400 bg-emerald-50 text-emerald-900", "#059669"],
  form: ["Form answered ✓", "border-emerald-400 bg-emerald-50 text-emerald-900", "#059669"],
  unsubscribed: ["Unsubscribed ✗", "border-rose-300 bg-rose-50 text-rose-900", "#e11d48"],
  stopped: ["Stopped", "border-slate-300 bg-slate-100 text-slate-700", "#64748b"],
  completed: ["Reminder cap reached", "border-slate-300 bg-slate-100 text-slate-700", "#64748b"],
  skipped: ["Skipped", "border-slate-300 bg-slate-100 text-slate-700", "#64748b"],
};

// ⭐ Review button — sits next to FIT in the order header (BO / SA, fulfilled orders). Opens the review dialog.
export function ReviewRequestButton({ order, user }) {
  const [open, setOpen] = useState(false);
  const staff = ["back_office", "super_admin"].includes(user?.role);
  const rr = order?.review_request || {};
  if (!staff || !(order?.status === "fulfilled" || rr.status)) return null;
  const [label, cls, fill] = STATUS[rr.status] || ["Photo review request — not sent yet", "border-slate-300 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800", "none"];
  const photos = (rr.photo_ids || []).length;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="order-review-btn" data-state={rr.status || "none"}
        title={`${label}${rr.send_count ? ` · sent ×${rr.send_count}` : ""}${photos ? ` · ${photos} photo${photos > 1 ? "s" : ""}` : ""} · click for details`}
        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs font-black tracking-widest transition-colors ${cls}`}>
        <Star size={14} fill={fill} strokeWidth={2.2} />
        REVIEW
        {rr.send_count > 0 && <span className="font-mono text-[10px] font-semibold opacity-80" data-testid="order-review-count">×{rr.send_count}</span>}
        {photos > 0 && <Camera size={13} data-testid="order-review-photos-icon" />}
      </button>
      {open && <ReviewRequestDialog order={order} onClose={() => setOpen(false)} label={label} />}
    </>
  );
}

function ReviewRequestDialog({ order, onClose, label }) {
  const qc = useQueryClient();
  const [urlDraft, setUrlDraft] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["review-request", order.id], queryFn: async () => (await api.get(`/orders/${order.id}/review-request`)).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["review-request", order.id] }); qc.invalidateQueries({ queryKey: ["order", order.id] }); qc.invalidateQueries({ queryKey: ["orders"] }); };
  const send = useMutation({
    mutationFn: async () => (await api.post(`/orders/${order.id}/review-request/send`)).data,
    onSuccess: (r) => { refresh(); toast.success(`Review request #${r.send_no} ${r.suppressed ? "logged (preview — not delivered)" : "sent"} · next reminder ${fmtDay(r.next_at)}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not send"),
  });
  const stop = useMutation({
    mutationFn: async () => (await api.post(`/orders/${order.id}/review-request/stop`, { reason: "Stopped by staff" })).data,
    onSuccess: () => { refresh(); toast.success("Review reminders stopped for this order"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not stop"),
  });
  const resume = useMutation({
    mutationFn: async () => (await api.post(`/orders/${order.id}/review-request/resume`)).data,
    onSuccess: () => { refresh(); toast.success("Review reminders resumed"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not resume"),
  });
  const saveUrl = useMutation({
    mutationFn: async (product_url) => (await api.patch(`/orders/${order.id}/review-request`, { product_url })).data,
    onSuccess: (r) => { refresh(); setUrlDraft(null); toast.success(r.product_source === "manual" ? "Product page saved" : `Product page found via ${r.product_source.replace("_", " ")}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save"),
  });
  const rr = data?.review_request || order.review_request || {};
  const sends = rr.sends || [];
  const photos = data?.photos || [];
  const inst = data?.installer;
  const active = rr.status === "sent";
  const statusLine = STATUS[rr.status]?.[0] || (data?.auto_eligible ? `Scheduled · ${fmtDay(data?.scheduled_at)}` : isLoading ? "Loading…" : "Not scheduled");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="review-request-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Star size={18} className="text-amber-500" fill="#f59e0b" /> Photo review · {order.po_number}</DialogTitle>
          <DialogDescription data-testid="review-request-status">
            {statusLine}
            {rr.send_count > 0 && <span data-testid="review-request-sends"> · sent ×{rr.send_count} · last {fmtDay(rr.last_sent_at)}{active && rr.next_at ? ` · next ${fmtDay(rr.next_at)}` : ""}</span>}
            {rr.clicked_at && <span> · clicked {fmtDay(rr.clicked_at)}{rr.click_count > 1 ? ` (×${rr.click_count})` : ""}</span>}
            {!rr.status && data?.auto_block_reason && !data?.auto_eligible && <span data-testid="review-request-block-reason"> · {data.auto_block_reason}</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Button size="sm" className="h-8 bg-amber-500 hover:bg-amber-600 text-black font-semibold" disabled={send.isPending || rr.status === "unsubscribed" || !data?.customer?.email}
            onClick={() => send.mutate()} data-testid="review-request-send-btn" title={!data?.customer?.email ? "Customer has no e-mail on file" : rr.send_count ? "Send the review e-mail again now" : "Send the review request now and start the reminder cycle"}>
            ⭐ {send.isPending ? "Sending…" : rr.send_count ? "Send again now" : "Send review request now"}
          </Button>
          {active && <Button size="sm" variant="outline" className="h-8" disabled={stop.isPending} onClick={() => stop.mutate()} data-testid="review-request-stop-btn">Stop reminders</Button>}
          {["stopped", "completed", "skipped"].includes(rr.status) && <Button size="sm" variant="outline" className="h-8" disabled={resume.isPending} onClick={() => resume.mutate()} data-testid="review-request-resume-btn">Resume</Button>}
          {data?.fit_confirmed && <span className="text-xs font-semibold text-emerald-800 ml-auto" data-testid="review-request-fit-confirmed" title={`${data.fit_confirmed.by} · ${fmt(data.fit_confirmed.at)}`}>✓ FIT signed by the customer ({data.fit_confirmed.via === "photos" ? "shared photos" : "review click"})</span>}
        </div>
        <div className="space-y-3 text-sm" data-testid="review-request-details">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-semibold">Review button opens:</span>
            <span className="uppercase tracking-wider font-mono">{data?.destination}</span>
            {data?.review_target && <a href={data.review_target} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1 break-all" data-testid="review-request-target-link">{data.review_target} <ExternalLink size={11} /></a>}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-semibold">Product page ({rr.product_source || "not looked up yet"}):</span>
            {urlDraft === null ? (
              <>
                <span className="break-all" data-testid="review-request-product-url">{rr.product_url || "—"}</span>
                {data?.product_is_search && <span className="rounded bg-rose-100 text-rose-800 border border-rose-300 px-1.5 py-0.5 font-semibold" data-testid="review-request-search-warning">⚠ search page, not the vehicle's product page — look it up or paste it</span>}
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setUrlDraft(rr.product_url || "")} data-testid="review-request-edit-url-btn">Edit</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={saveUrl.isPending} onClick={() => saveUrl.mutate("")} data-testid="review-request-lookup-btn">🔍 Find product page</Button>
              </>
            ) : (
              <>
                <Input value={urlDraft} onChange={(e) => setUrlDraft(e.target.value)} placeholder="https://leprecon.plus/products/…" className="h-8 text-xs max-w-md" data-testid="review-request-url-input" />
                <Button size="sm" className="h-7 text-xs" disabled={saveUrl.isPending || !urlDraft.trim()} onClick={() => saveUrl.mutate(urlDraft.trim())} data-testid="review-request-url-save">Save</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setUrlDraft(null)}>Cancel</Button>
              </>
            )}
          </div>
          {data?.customer && (
            <div className="text-xs">To: <b>{data.customer.name}</b> · {data.customer.email || <span className="text-rose-700">no e-mail on file</span>}{data.customer.review_unsubscribed_at ? " · unsubscribed from review e-mails" : ""}</div>
          )}
          {(rr.stop_reason || rr.skipped_reason) && <div className="text-xs">Reason: {rr.stop_reason || rr.skipped_reason}</div>}
          {(sends.length > 0 || (data?.emails || []).some((e) => e.template_key === "photos_received_internal")) && (
            <table className="text-xs w-full" data-testid="review-request-sends-table">
              <tbody>
                {[...sends].reverse().map((s, i) => (
                  <tr key={i} className="border-t border-black/10">
                    <td className="py-1 pr-3 whitespace-nowrap">#{s.no}</td>
                    <td className="py-1 pr-3 whitespace-nowrap">{fmt(s.at)}</td>
                    <td className="py-1 pr-3">{s.subject}</td>
                    <td className="py-1 pr-3 whitespace-nowrap font-mono">{s.manual ? "manual" : "auto"}{s.suppressed ? " · preview" : ""}</td>
                  </tr>
                ))}
                {(data?.emails || []).filter((e) => e.template_key === "photos_received_internal").map((e) => (
                  <tr key={e.id} className="border-t border-black/10" data-testid="review-request-photos-email-row">
                    <td className="py-1 pr-3 whitespace-nowrap">📸</td>
                    <td className="py-1 pr-3 whitespace-nowrap">{fmt(e.sent_at)}</td>
                    <td className="py-1 pr-3">Client responded — notification to {e.to}{e.photos ? ` (${e.photos} photo${e.photos > 1 ? "s" : ""})` : ""}</td>
                    <td className="py-1 pr-3 whitespace-nowrap font-mono">shop{e.suppressed ? " · preview" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {inst && (
            <div className="rounded border border-black/10 bg-slate-50 p-3 text-xs" data-testid="review-request-installer">
              <div className="font-semibold mb-1 inline-flex items-center gap-1"><Wrench size={12} /> {inst.installed_by === "self" ? "Customer installed the covers themselves" : "Installed by a mechanic / shop"}</div>
              {inst.installed_by === "mechanic" && (
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5">
                  <div>Shop: <b>{inst.shop_name || "—"}</b></div><div>Mechanic: {inst.mechanic_name || "—"}</div>
                  <div>Address: {[inst.address, inst.city, inst.province].filter(Boolean).join(", ") || "—"}</div><div>Phone: {inst.phone || "—"}</div>
                  <div>Recommend: {inst.recommend || "—"}</div>
                </div>
              )}
              {inst.notes && <div className="mt-1 italic">“{inst.notes}”</div>}
              <div className="mt-1 opacity-70">{fmt(inst.created_at)} · also listed in the Installers tab</div>
            </div>
          )}
          {photos.length > 0 && (
            <div data-testid="review-request-photos">
              <div className="text-xs font-semibold mb-1 flex flex-wrap items-center gap-1">
                <Camera size={12} /> Photos shared by the customer
                {rr.photo_consent === false ? " · NO permission to publish" : rr.photo_consent ? " · OK to publish" : ""}
                {" · "}
                {photos.every((p) => p.drive_file_id)
                  ? (data?.drive_folder_url ? <a href={data.drive_folder_url} target="_blank" rel="noreferrer" className="underline" data-testid="review-request-drive-link">saved in the order's Drive folder</a> : "saved in the order's Drive folder")
                  : <span className="text-amber-700" data-testid="review-request-drive-pending">{photos.filter((p) => p.drive_file_id).length}/{photos.length} in Drive — rest retried hourly</span>}
                {rr.photos_notified_at ? ` · shop notified ${fmt(rr.photos_notified_at)}` : ` · shop notification to ${data?.photos_notify_email} pending`}
              </div>
              <div className="flex flex-wrap gap-2">
                {photos.map((p) => (
                  <a key={p.id} href={p.drive_url || `${process.env.REACT_APP_BACKEND_URL}/api/review-photos/${p.id}`} target="_blank" rel="noreferrer" title={p.name} data-testid={`review-photo-${p.id}`}>
                    <ReviewPhoto id={p.id} name={p.name} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReviewPhoto({ id, name }) {
  const [src, setSrc] = useState(null);
  React.useEffect(() => {
    let url;
    api.get(`/review-photos/${id}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [id]);
  return src ? <img src={src} alt={name} className="h-24 w-24 object-cover rounded border border-black/10" /> : <div className="h-24 w-24 rounded border border-black/10 bg-slate-100 animate-pulse" />;
}
